import { useEffect, useRef, useState } from 'react';

import { ROUTE42_ASSISTANT_NAME } from '../../../config/identity';

export function InvestigationComposer({
  canContribute,
  canAsk,
  variant = 'advanced',
  focusRequest,
  busyAction,
  onPostUpdate,
  onAskAce,
}: {
  canContribute: boolean;
  canAsk: boolean;
  variant?: 'operational' | 'advanced';
  focusRequest?: { destination: 'team' | 'ace'; requestId: number } | null;
  busyAction: 'post' | 'ask' | null;
  onPostUpdate: (text: string) => Promise<void> | void;
  onAskAce: (text: string) => Promise<void> | void;
}) {
  const [value, setValue] = useState('');
  const [destination, setDestination] = useState<'team' | 'ace'>('team');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lastHandledFocusRequest = useRef<number | null>(null);
  useEffect(() => {
    if (variant !== 'operational' || !focusRequest || !canContribute || busyAction !== null) return;
    if (focusRequest.destination === 'ace' && !canAsk) return;
    if (lastHandledFocusRequest.current === focusRequest.requestId) return;
    lastHandledFocusRequest.current = focusRequest.requestId;
    setDestination(focusRequest.destination);
    textareaRef.current?.focus();
  }, [focusRequest, variant, canContribute, canAsk, busyAction]);
  const hasText = Boolean(value.trim());
  const busy = busyAction !== null;
  const disabled = !canContribute || busy;

  async function submit(kind: 'post' | 'ask') {
    const text = value.trim();
    if (!text || busy) return;
    if (kind === 'post' && !canContribute) return;
    if (kind === 'ask' && !canAsk) return;
    setValue('');
    try {
      if (kind === 'post') await onPostUpdate(text);
      else await onAskAce(text);
    } catch {
      setValue(text);
    }
  }

  if (!canContribute) {
    return (
      <div className="r42-investigation-composer r42-investigation-composer--readonly" aria-label="Investigation contribution controls">
        <p><i className="bi bi-eye" aria-hidden="true" /> You can view this Investigation. Contributors can post team updates and ask {ROUTE42_ASSISTANT_NAME}.</p>
      </div>
    );
  }

  return (
    <div className="r42-investigation-composer" aria-label="Investigation contribution controls">
      {variant === 'operational' ? <div className="r42-investigation-composer__destination" role="group" aria-label="Message destination">
        <span className="r42-investigation-composer__destination-label">Send to</span>
        <button type="button" aria-pressed={destination === 'team'} onClick={() => setDestination('team')} disabled={busy}>Team</button>
        <button type="button" aria-pressed={destination === 'ace'} onClick={() => setDestination('ace')} disabled={busy || !canAsk}>Ace</button>
      </div> : null}
      <textarea
        ref={textareaRef}
        aria-label="Investigation update or question"
        value={value}
        disabled={disabled}
        placeholder={variant === 'operational' ? destination === 'team' ? 'Share an observation or update with your team…' : 'Ask Ace about the investigation or machine evidence…' : 'Add an update or ask a question...'}
        rows={2}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && !event.nativeEvent.isComposing) {
            event.preventDefault();
            void submit(variant === 'operational' ? destination === 'team' ? 'post' : 'ask' : 'post');
          }
        }}
      />
      <div className="r42-investigation-composer__actions">
        {variant === 'operational' ? <button
          type="button"
          className="btn r42-btn-primary r42-investigation-post-button"
          disabled={busy || !hasText || (destination === 'ace' && !canAsk)}
          aria-busy={busy || undefined}
          onClick={() => void submit(destination === 'team' ? 'post' : 'ask')}
        >
          {busy ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <i className="bi bi-send" aria-hidden="true" />}
          <span>{destination === 'team' ? 'Send to team' : `Ask ${ROUTE42_ASSISTANT_NAME}`}</span>
        </button> : <>
        <button
          type="button"
          className="btn r42-btn-primary r42-investigation-post-button"
          disabled={busy || !hasText}
          aria-busy={busyAction === 'post' || undefined}
          onClick={() => void submit('post')}
        >
          {busyAction === 'post' ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <i className="bi bi-send" aria-hidden="true" />}
          <span>Post update</span>
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary r42-investigation-ask-button"
          disabled={!canAsk || busy || !hasText}
          aria-busy={busyAction === 'ask' || undefined}
          onClick={() => void submit('ask')}
        >
          {busyAction === 'ask' ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <i className="bi bi-compass" aria-hidden="true" />}
          <span>Ask {ROUTE42_ASSISTANT_NAME}</span>
        </button>
        </>}
      </div>
      <p>{variant === 'operational'
        ? destination === 'team' ? 'Your update is shared with the investigation team; it does not automatically authorize reassessment. Ctrl/⌘ + Enter to send.' : `${ROUTE42_ASSISTANT_NAME} provides advisory answers using available investigation and machine evidence; chatting does not approve maintenance actions. Ctrl/⌘ + Enter to send.`
        : `Post update shares with the team. Ask ${ROUTE42_ASSISTANT_NAME} also requests AI reasoning over the Investigation and grounded machine evidence.`}</p>
    </div>
  );
}
